import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { MessageSquare, Heart, Bell } from 'lucide-react';
import axios from 'axios';

// Import the existing page content as components
import MessagesContent from './MessagesContent';
import CommunityContent from './CommunityContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommunicationsHub = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('messages');
  const [stats, setStats] = useState({ unread_announcements: 0 });
  const [dmUnreadCount, setDmUnreadCount] = useState(0);

  // Determine initial tab from URL or state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'community' || tab === 'messages') {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Fetch unread counts for badges
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsRes, dmRes] = await Promise.all([
          axios.get(`${API_URL}/api/communications/stats`, { withCredentials: true }),
          axios.get(`${API_URL}/api/dm/unread-count`, { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setDmUnreadCount(dmRes.data.unread_count || 0);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/communications?tab=${tab}`, { replace: true });
  };

  const totalUnread = (stats.unread_announcements || 0) + dmUnreadCount;

  return (
    <ParentLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="communications-hub">
        {/* Header - Athletic Style */}
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Stay Connected</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-navy uppercase tracking-tighter mb-1">Communications</h1>
          <p className="text-gray-600 font-body">Park City Chiefs announcements and community</p>
        </div>

        {/* Tabs - Athletic Style */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-gray-100 p-1.5 rounded-xl border border-gray-200">
            <TabsTrigger 
              value="messages" 
              className="flex items-center justify-center gap-2 data-[state=active]:bg-navy data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg py-3 font-bold uppercase tracking-wider text-xs transition-all duration-200"
              data-testid="tab-messages"
            >
              <MessageSquare className="w-4 h-4" />
              <span>Messages</span>
              {totalUnread > 0 && (
                <span className="ml-1 bg-red text-white text-[10px] font-bold px-2 py-0.5 rounded-full min-w-[20px]">
                  {totalUnread}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="community" 
              className="flex items-center justify-center gap-2 data-[state=active]:bg-navy data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg py-3 font-bold uppercase tracking-wider text-xs transition-all duration-200"
              data-testid="tab-community"
            >
              <Heart className="w-4 h-4" />
              <span>Community</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="messages" className="mt-0">
            <MessagesContent />
          </TabsContent>

          <TabsContent value="community" className="mt-0">
            <div
              className="rounded-xl p-4"
              style={{
                backgroundImage: `url('https://customer-assets.emergentagent.com/job_d8bf1b61-a9b7-4610-b686-61792bda2646/artifacts/ukktq97e_App_icon-Background_1.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            >
              <CommunityContent />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </ParentLayout>
  );
};

export default CommunicationsHub;
